import { API_AUTH_LOGIN_URL, API_AUTH_LOGOUT_URL, API_AUTH_SESSION_URL } from '@/consts/baseUrls'
import type { AuthRole, AuthUser, RequiredTermsVersion } from '@/types/auth'
import axios, { axiosPkg } from '@/utils/axios'

export type AuthClientLoginResult = { ok: true } | { ok: false; status?: number; error: string }

type AuthSessionResult = {
    user: AuthUser
    expiresAt: string
}

type AuthSessionResponse = {
    user?: {
        id: string
        organizationId: string
        email: string
        role: AuthRole
        hasAcceptedLatestTerms: boolean
        requiredTermsVersion: RequiredTermsVersion | null
    } | null
    expiresAt?: string
}

/**
 * Centralized API client for authentication endpoints.
 */
export const AuthClient = {
    /**
     * Attempts to authenticate with email/password and returns a typed status result.
     */
    async login(email: string, password: string): Promise<AuthClientLoginResult> {
        try {
            await axios.post(
                API_AUTH_LOGIN_URL,
                {
                    email,
                    password,
                },
                {
                    withCredentials: true,
                },
            )

            return { ok: true }
        } catch (error) {
            if (axiosPkg.isAxiosError(error)) {
                return {
                    ok: false,
                    status: error.response?.status,
                    error: getAxiosErrorMessage(error, 'LOGIN_FAILED'),
                }
            }

            return { ok: false, error: 'LOGIN_FAILED' }
        }
    },

    /**
     * Invalidates the active server-side session cookie.
     */
    async logout(): Promise<void> {
        try {
            await axios.post(API_AUTH_LOGOUT_URL, null, {
                withCredentials: true,
            })
        } catch (_error) {
            // Logout should not block UI transitions.
        }
    },

    /**
     * Fetches the current authenticated session payload.
     */
    async getSession(): Promise<AuthSessionResult | null> {
        try {
            const response = await axios.get<AuthSessionResponse>(API_AUTH_SESSION_URL, {
                withCredentials: true,
            })

            const user = response.data.user
            if (!user) {
                return null
            }

            return {
                user: toAuthUser(user),
                expiresAt: response.data.expiresAt ?? '',
            }
        } catch (error) {
            if (axiosPkg.isAxiosError(error) && error.response?.status === 401) {
                return null
            }

            throw error
        }
    },
}

/**
 * Maps unknown axios error payloads to a stable message string.
 */
const getAxiosErrorMessage = (error: unknown, fallback: string): string => {
    if (!axiosPkg.isAxiosError(error)) {
        return fallback
    }

    const responseError = error.response?.data
    if (typeof responseError === 'string' && responseError.trim().length > 0) {
        return responseError
    }

    if (responseError && typeof responseError === 'object' && 'error' in responseError) {
        const nestedError = (responseError as { error?: unknown }).error
        if (typeof nestedError === 'string' && nestedError.trim().length > 0) {
            return nestedError
        }
    }

    return fallback
}

/**
 * Transforms wire-format session users into shared AuthUser shape.
 */
const toAuthUser = (user: NonNullable<AuthSessionResponse['user']>): AuthUser => {
    return {
        id: user.id,
        organizationId: user.organizationId,
        email: user.email,
        role: user.role,
        hasAcceptedLatestTerms: user.hasAcceptedLatestTerms,
        requiredTermsVersion: user.requiredTermsVersion,
    }
}
